import React, { useState, useRef, useLayoutEffect } from 'react';
import FeatherIcon from 'feather-icons-react';
import LogoIcon from '../../assets/images/extension/logo-purple-icon.png';

// Popovers are centered on their anchor word by default, which overflows the
// viewport when the word sits near an edge on narrow (mobile) screens. This
// measures the popover after mount and nudges it back on-screen.
const VIEWPORT_MARGIN = 12;
function useKeepInViewport() {
  const ref = useRef(null);
  const [shiftX, setShiftX] = useState(0);

  useLayoutEffect(() => {
    const measure = () => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const overflowRight = rect.right - (window.innerWidth - VIEWPORT_MARGIN);
      const overflowLeft = VIEWPORT_MARGIN - rect.left;
      if (overflowRight > 0) setShiftX((s) => s - overflowRight);
      else if (overflowLeft > 0) setShiftX((s) => s + overflowLeft);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  return { ref, shiftX };
}

// Sample data mirrors the real extension's lexical entry shape (senses, forms, nuances, phrases).
// Only TARGET_WORD starts unsaved — it's the one the demo instructs the user to click and save.
// The other paragraph words start already saved/highlighted, like real pages after you've used the extension a while.
const TARGET_WORD = {
  key: 'mild',
  display_word: 'mild',
  pronunciation: 'maɪld',
  translation: '穏やかな',
  cefr_level: 'A2',
  category: 'daily life',
  image_url: 'https://images.pexels.com/photos/6913391/pexels-photo-6913391.jpeg',
  audio_url: 'https://audio.convolines.co/hv_lexical_entries/3943.mp3',
  senses: [
    { part_of_speech: 'adjective', definition: 'not strong or severe; gentle or mild in degree', example: 'The doctor said it was just a mild cold.' },
    { part_of_speech: 'adjective', definition: 'not strong in flavor or spice; gentle taste', example: 'This curry has a mild, comforting flavor.' },
  ],
  forms: [
    { form: 'mild', is_primary: true },
    { form: 'milder' },
    { form: 'mildest' },
    { form: 'mildly' },
  ],
};

// Already-saved paragraph words — shown highlighted from the start, hover-only (no click-to-save).
const PARAGRAPH_SAVED_WORDS = [
  {
    key: 'vault',
    display_word: 'vault',
    pronunciation: 'vɔːlt',
    translation: '金庫',
    cefr_level: 'B2',
    category: 'other',
    image_url: 'https://images.pexels.com/photos/60611/vaulted-cellar-tunnel-arches-keller-60611.jpeg',
    audio_url: 'https://audio.convolines.co/hv_lexical_entries/4319.mp3',
    senses: [
      { part_of_speech: 'noun', definition: 'a secure room for money or valuables.', example: 'The bank vault kept the cash safe.' },
      { part_of_speech: 'verb', definition: 'to jump over or leap over something.', example: 'The gymnast vaulted over the hurdle.' },
    ],
    forms: [
      { form: 'vault', is_primary: true },
      { form: 'vaults' },
      { form: 'vaulted' },
      { form: 'vaulting' },
    ],
    related_nuances: ['safe', 'strongroom', 'cache', 'leap', 'jump'],
    often_appears_as: ['she unlocked the bank vault', 'the vault is secure', 'vault over the gate'],
  },
  {
    key: 'schedule',
    display_word: 'schedule',
    pronunciation: 'ˈskedʒuːl',
    translation: 'スケジュール',
    cefr_level: 'B2',
    category: 'work & business',
    image_url: 'https://images.pexels.com/photos/768472/pexels-photo-768472.jpeg',
    audio_url: 'https://audio.convolines.co/hv_lexical_entries/6168.mp3',
    senses: [
      { part_of_speech: 'noun', definition: 'a plan of times when events will happen', example: 'We looked at the project schedule and booked the rooms.' },
      { part_of_speech: 'verb', definition: 'to arrange for something to happen at a specific time', example: 'I will schedule a meeting for tomorrow.' },
    ],
    forms: [
      { form: 'schedule', is_primary: true },
      { form: 'schedules' },
      { form: 'scheduled' },
      { form: 'scheduling' },
    ],
    related_nuances: ['plan', 'timetable', 'calendar', 'agenda', 'itinerary'],
    often_appears_as: ['I need to schedule a meeting', 'we have a busy schedule today', "let's schedule a call next week"],
  },
  {
    key: 'innovative',
    display_word: 'innovative',
    pronunciation: 'ˈɪnəˌveɪtɪv',
    translation: '革新的',
    cefr_level: 'B2',
    category: 'technology',
    image_url: 'https://images.pexels.com/photos/1314410/pexels-photo-1314410.jpeg',
    audio_url: 'https://audio.convolines.co/hv_lexical_entries/1134.mp3',
    senses: [
      { part_of_speech: 'adjective', definition: 'introducing new ideas or methods', example: 'An innovative approach to teaching.' },
      { part_of_speech: 'adjective', definition: 'showing creativity and originality', example: 'They praised the team for innovative thinking.' },
    ],
    forms: [
      { form: 'innovative', is_primary: true },
      { form: 'innovatively' },
    ],
    related_nuances: ['novel', 'inventive', 'groundbreaking', 'pioneering', 'original'],
    often_appears_as: ['an innovative solution to a problem', 'the innovative design stood out', 'innovative ideas shaped the project'],
  },
  {
    key: 'relentless',
    display_word: 'relentless',
    pronunciation: 'riˈlentləs',
    translation: '容赦ない',
    cefr_level: 'B2',
    category: 'emotion',
    image_url: 'https://images.pexels.com/photos/16838917/pexels-photo-16838917.jpeg',
    audio_url: 'https://audio.convolines.co/hv_lexical_entries/4599.mp3',
    senses: [
      { part_of_speech: 'adjective', definition: 'not stopping; continuing with great energy', example: 'The rain was relentless and kept coming down all night.' },
      { part_of_speech: 'adjective', definition: 'harsh or merciless in demand or persistence', example: 'Her relentless critics demanded more proof.' },
    ],
    forms: [
      { form: 'relentless', is_primary: true },
      { form: 'relentlessly' },
      { form: 'relentlessness' },
    ],
    related_nuances: ['merciless', 'unyielding', 'persistent', 'incessant', 'harsh'],
    often_appears_as: ['her pursuit was relentless', 'he faced relentless pressure daily', 'they endured relentless criticism together'],
  },
];

const INITIAL_SAVED = [...PARAGRAPH_SAVED_WORDS];

const cefrColor = { A1: '#059669', A2: '#059669', B1: '#7c3aed', B2: '#7c3aed', C1: '#d97706', C2: '#dc2626' };

const synth = typeof window !== 'undefined' ? window.speechSynthesis : null;
const pronounce = (text) => {
  if (!synth) return;
  const uttr = new SpeechSynthesisUtterance(text);
  uttr.rate = 0.85;
  synth.speak(uttr);
};
// Mirrors the real extension's playPronunciation: prefer the recorded audio_url, fall back to TTS.
const speak = (audioUrl, text) => {
  if (audioUrl) {
    const audio = new Audio(audioUrl);
    audio.play().catch(() => pronounce(text));
    return;
  }
  pronounce(text);
};

const highlightWord = (text, word) => {
  const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === word.toLowerCase()
      ? <strong key={i} style={{ color: '#7c3aed', fontStyle: 'normal' }}>{part}</strong>
      : part
  );
};

const WORD_POOL = [TARGET_WORD, ...PARAGRAPH_SAVED_WORDS];

function TryIt({ savedKeys, selectedKey, popoverKey, hoverKey, onWordSelect, onIconClick, onClosePopover, onWordHover, onSave }) {
  const parts = [
    'The startup’s founder, known for her ',
    { key: 'relentless' },
    ' drive, gave a confident interview about her team’s ',
    { key: 'innovative' },
    ' new app.',
    { br: true },
    'Her tone was surprisingly ',
    { key: 'mild' },
    ' — then she rushed off to check her ',
    { key: 'schedule' },
    ' before locking the prototype in a ',
    { key: 'vault' },
    '.',
  ];

  return (
    <p style={{ fontSize: '1.05rem', lineHeight: 2.6, color: '#334155', margin: 0 }}>
      {parts.map((part, i) => {
        if (typeof part === 'string') return <React.Fragment key={i}>{part}</React.Fragment>;
        if (part.br) return <br key={i} />;
        const word = WORD_POOL.find((w) => w.key === part.key);
        const isTarget = word.key === TARGET_WORD.key;
        const saved = savedKeys.has(word.key);
        const selected = selectedKey === word.key;
        return (
          <span
            key={i}
            style={{ position: 'relative', display: 'inline-block' }}
            onMouseEnter={() => saved && onWordHover(word.key)}
            onMouseLeave={() => saved && onWordHover(null)}
          >
            <span
              onClick={() => isTarget && !saved && onWordSelect(word.key)}
              style={{
                cursor: isTarget && !saved ? 'pointer' : 'default',
                color: saved ? '#7c3aed' : '#0f172a',
                fontWeight: saved ? 700 : isTarget ? 600 : 400,
                borderBottom: saved ? '2px solid #7c3aed' : 'none',
                background: isTarget && !saved ? (selected ? '#c4b5fd' : '#fef08a') : 'transparent',
                borderRadius: isTarget && !saved ? 4 : 0,
                padding: isTarget && !saved ? '1px 3px' : 0,
                transition: 'all 0.2s ease',
              }}
            >
              {word.display_word}
            </span>
            {isTarget && !saved && !selected && popoverKey !== word.key && (
              <span
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  marginTop: 6,
                  zIndex: 20,
                  whiteSpace: 'nowrap',
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: '#7c3aed',
                  background: '#ede9fe',
                  padding: '3px 9px',
                  borderRadius: 999,
                  pointerEvents: 'none',
                  animation: 'hv-bounce 1.6s ease-in-out infinite',
                }}
              >
                👆 Click on word to select
              </span>
            )}
            {selected && (
              <button
                onClick={(e) => { e.stopPropagation(); onIconClick(word.key); }}
                aria-label="Look up with HighVocab"
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  marginTop: 6,
                  zIndex: 20,
                  width: 30,
                  height: 30,
                  borderRadius: 10,
                  border: 'none',
                  background: '#8d29da',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  animation: 'hv-bounce 1.2s ease-in-out infinite',
                }}
              >
                <img src={LogoIcon} alt="" style={{ width: 18, height: 18 }} />
              </button>
            )}
            {popoverKey === word.key && (
              <LookupPopover word={word} onSave={() => onSave(word.key)} onClose={onClosePopover} />
            )}
            {hoverKey === word.key && <HoverPreview word={word} />}
          </span>
        );
      })}
    </p>
  );
}

function LookupPopover({ word, onSave, onClose }) {
  const sense = word.senses[0];
  const { ref, shiftX } = useKeepInViewport();
  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        top: '100%',
        left: '50%',
        transform: `translateX(calc(-50% + ${shiftX}px))`,
        marginTop: 8,
        zIndex: 20,
        width: 240,
        maxWidth: 'calc(100vw - 48px)',
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 14,
        boxShadow: '0 12px 28px rgba(15,23,42,0.16)',
        overflow: 'hidden',
        lineHeight: 'normal',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 8px 6px 12px', borderBottom: '1px solid #f3f4f6' }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: '#7c3aed', textTransform: 'uppercase' }}>HighVocab</span>
        <button
          onClick={onClose}
          aria-label="Close"
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', padding: 4 }}
        >
          <FeatherIcon icon="x" style={{ width: 14, height: 14 }} />
        </button>
      </div>
      <div style={{ padding: '10px 12px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{word.display_word}</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#7c3aed', background: '#ede9fe', padding: '1px 6px', borderRadius: 4 }}>
            {word.cefr_level}
          </span>
        </div>
        <span style={{ fontSize: 11, color: '#9ca3af' }}>/{word.pronunciation}/</span>
        <span style={{ fontSize: 12, fontWeight: 500, color: '#7c3aed' }}>{word.translation}</span>
        <p style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5, margin: 0 }}>{sense.definition}</p>
        <button
          onClick={onSave}
          style={{ width: '100%', padding: '7px 0', marginTop: 4, borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
        >
          Save word
        </button>
      </div>
    </div>
  );
}

// Mirrors the real content-script hover tooltip shown on already-highlighted words across the web.
function HoverPreview({ word }) {
  const sense = word.senses[0];
  const { ref, shiftX } = useKeepInViewport();
  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        bottom: '100%',
        left: '50%',
        transform: `translateX(calc(-50% + ${shiftX}px))`,
        marginBottom: 10,
        zIndex: 20,
        width: 260,
        maxWidth: 'calc(100vw - 48px)',
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 14,
        boxShadow: '0 12px 28px rgba(15,23,42,0.18)',
        padding: '12px 14px',
        pointerEvents: 'none',
        lineHeight: 'normal',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 6, marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{word.display_word}</span>
          <span style={{ fontSize: 12, fontWeight: 500, color: '#7c3aed' }}>{word.translation}</span>
        </div>
        <span style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase' }}>{sense.part_of_speech}</span>
      </div>
      <span style={{ fontSize: 11, color: '#9ca3af' }}>/{word.pronunciation}/</span>
      <p style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5, margin: '4px 0 0' }}>{sense.definition}</p>
      {sense.example && (
        <p style={{ fontSize: 11.5, color: '#94a3b8', fontStyle: 'italic', margin: '4px 0 0' }}>&quot;{sense.example}&quot;</p>
      )}
      {word.often_appears_as && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f3f4f6' }}>
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', color: '#a78bfa', textTransform: 'uppercase' }}>Commonly used as</span>
          {word.often_appears_as.slice(0, 2).map((p, i) => (
            <p key={i} style={{ fontSize: 11, color: '#6b7280', margin: '3px 0 0' }}>• {highlightWord(p, word.display_word)}</p>
          ))}
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderTop: '6px solid #fff',
        }}
      />
    </div>
  );
}

function PopupTabBar() {
  const tabs = [
    { id: 'home', label: 'Home', icon: 'home', disabled: true },
    { id: 'discover', label: 'Discover', icon: 'compass', disabled: true },
    { id: 'mywords', label: 'My Words', icon: 'bookmark', disabled: false },
    { id: 'notes', label: 'Notes', icon: 'edit-3', disabled: true },
  ];
  return (
    <div style={{ display: 'flex', borderTop: '1px solid #f1f3f5', background: '#fff', padding: '6px 4px 8px' }}>
      {tabs.map(({ id, label, icon, disabled }) => (
        <div key={id} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, opacity: disabled ? 0.35 : 1, cursor: disabled ? 'not-allowed' : 'default' }}>
          <div style={{ width: 40, height: 26, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: !disabled ? 'rgba(124,58,237,0.1)' : 'transparent' }}>
            <FeatherIcon icon={icon} style={{ width: 16, height: 16, color: !disabled ? '#7c3aed' : '#94a3b8' }} />
          </div>
          <span style={{ fontSize: 9, fontWeight: 600, color: !disabled ? '#7c3aed' : '#94a3b8' }}>{label}</span>
        </div>
      ))}
    </div>
  );
}

function WordRow({ word, flash, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 14px',
        borderBottom: '1px solid #f1f3f5',
        background: flash ? '#f5f3ff' : 'transparent',
        transition: 'background 1.4s ease',
        cursor: 'pointer',
      }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); speak(word.audio_url, word.display_word); }}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', color: '#9ca3af' }}
      >
        <FeatherIcon icon="volume-2" style={{ width: 15, height: 15 }} />
      </button>
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{word.display_word}</span>
          <span style={{ fontSize: 10.5, color: '#9ca3af' }}>/{word.pronunciation}/</span>
        </div>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>{word.translation}</span>
      </div>
      <span style={{ fontSize: 9, fontWeight: 700, color: cefrColor[word.cefr_level] || '#7c3aed', background: '#f5f3ff', padding: '2px 6px', borderRadius: 4 }}>
        {word.cefr_level}
      </span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: '#94a3b8', textTransform: 'uppercase', margin: '0 0 8px' }}>
        {title}
      </p>
      {children}
    </div>
  );
}

// Full curated word detail — mirrors the extension's Word Detail screen (image, every sense, forms, nuances, phrases).
function WordDetail({ word, onBack }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderBottom: '1px solid #f1f3f5', flexShrink: 0 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', color: '#64748b', padding: 4 }}>
          <FeatherIcon icon="arrow-left" style={{ width: 16, height: 16 }} />
        </button>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: '#94a3b8', textTransform: 'uppercase' }}>Word Detail</span>
      </div>
      <div style={{ padding: '14px 16px 20px', overflowY: 'auto', flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
          <div>
            <h4 style={{ fontSize: 26, fontWeight: 700, color: '#0f172a', margin: 0, lineHeight: 1.1 }}>{word.display_word}</h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
              <span style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>/{word.pronunciation}/</span>
              <button onClick={() => speak(word.audio_url, word.display_word)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', color: '#94a3b8' }}>
                <FeatherIcon icon="volume-2" style={{ width: 13, height: 13 }} />
              </button>
            </div>
          </div>
          <div style={{ background: '#f5f3ff', borderRadius: 8, padding: '6px 10px', textAlign: 'right' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{word.translation}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4, marginBottom: 14, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#7c3aed', background: '#ede9fe', padding: '2px 7px', borderRadius: 5 }}>{word.cefr_level}</span>
          {word.category && (
            <span style={{ fontSize: 10, fontWeight: 600, color: '#64748b', background: '#f1f5f9', padding: '2px 7px', borderRadius: 5, textTransform: 'capitalize' }}>{word.category}</span>
          )}
        </div>

        {word.image_url && (
          <div style={{ borderRadius: 12, overflow: 'hidden', marginBottom: 16, height: 130 }}>
            <img src={word.image_url} alt={word.display_word} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </div>
        )}

        <Section title="Definitions">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {word.senses.map((sense, i) => (
              <div key={i} style={{ borderRadius: 10, background: '#fafafa', border: '1px solid #f1f3f5', borderLeft: '3px solid #7c3aed', padding: '9px 12px' }}>
                <p style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.06em', color: '#7c3aed', textTransform: 'uppercase', margin: '0 0 4px' }}>
                  {sense.part_of_speech}
                </p>
                <p style={{ fontSize: 12.5, color: '#334155', margin: '0 0 4px', lineHeight: 1.5 }}>{sense.definition}</p>
                {sense.example && (
                  <p style={{ fontSize: 11.5, color: '#94a3b8', fontStyle: 'italic', margin: 0 }}>&quot;{sense.example}&quot;</p>
                )}
              </div>
            ))}
          </div>
        </Section>

        {word.forms && (
          <Section title="Word Forms">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {word.forms.map((f, i) => (
                <span
                  key={i}
                  style={{
                    fontSize: 11.5,
                    padding: '4px 10px',
                    borderRadius: 999,
                    border: '1px solid',
                    borderColor: f.is_primary ? 'rgba(124,58,237,0.35)' : '#e2e8f0',
                    background: f.is_primary ? 'rgba(124,58,237,0.08)' : '#f8fafc',
                    color: f.is_primary ? '#7c3aed' : '#64748b',
                    fontWeight: f.is_primary ? 600 : 400,
                  }}
                >
                  {f.form}
                </span>
              ))}
            </div>
          </Section>
        )}

        {word.related_nuances && (
          <Section title="Related Nuances">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {word.related_nuances.map((n, i) => (
                <span key={i} style={{ fontSize: 11.5, padding: '4px 10px', borderRadius: 999, background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b' }}>
                  {n}
                </span>
              ))}
            </div>
          </Section>
        )}

        {word.often_appears_as && (
          <Section title="Commonly Used">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {word.often_appears_as.map((ex, i) => (
                <div key={i} style={{ background: '#fafafa', border: '1px solid #f1f3f5', borderRadius: 8, padding: '7px 10px' }}>
                  <span style={{ fontSize: 12, color: '#475569', fontStyle: 'italic' }}>{highlightWord(ex, word.display_word)}</span>
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

export default function ExtensionDemo() {
  const [savedWords, setSavedWords] = useState(INITIAL_SAVED);
  const [savedKeys, setSavedKeys] = useState(new Set(INITIAL_SAVED.map((w) => w.key)));
  const [selectedKey, setSelectedKey] = useState(null); // word "selected" — shows the floating HighVocab icon
  const [popoverKey, setPopoverKey] = useState(null); // icon clicked — shows the lookup/save popover
  const [hoverKey, setHoverKey] = useState(null);
  const [flashKey, setFlashKey] = useState(null);
  const [detailKey, setDetailKey] = useState(null);

  const handleWordSelect = (key) => {
    setSelectedKey((prev) => (prev === key ? null : key));
    setPopoverKey(null);
  };
  const handleIconClick = (key) => {
    setPopoverKey(key);
    setSelectedKey(null);
  };
  const handleClosePopover = () => setPopoverKey(null);

  const handleSave = (key) => {
    const word = WORD_POOL.find((w) => w.key === key);
    setSavedWords((prev) => [...prev, word]);
    setSavedKeys((prev) => new Set([...prev, key]));
    setPopoverKey(null);
    setSelectedKey(null);
    setFlashKey(key);
    setTimeout(() => setFlashKey(null), 1400);
  };

  const detailWord = savedWords.find((w) => w.key === detailKey);

  return (
    <div
      style={{ maxWidth: 980, margin: '0 auto' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          setPopoverKey(null);
          setSelectedKey(null);
        }
      }}
    >
      <style>{`
        @keyframes hv-bounce {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50% { transform: translateX(-50%) translateY(-4px); }
        }
      `}</style>
      <div className="row g-4 align-items-start">
        {/* "webpage" panel */}
        <div className="col-lg-7">
          <div
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 16,
              boxShadow: '0 8px 28px rgba(15,23,42,0.06)',
              overflow: 'visible',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderBottom: '1px solid #f1f3f5' }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#fca5a5' }} />
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#fde68a' }} />
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#86efac' }} />
              <span style={{ marginLeft: 10, fontSize: 11, color: '#94a3b8' }}>techdaily.example.com/article</span>
            </div>
            <div style={{ padding: '28px 28px 32px' }}>
              <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: '#a78bfa', textTransform: 'uppercase', margin: '0 0 10px' }}>
                Try it — select the word &quot;mild&quot; below, then tap the HighVocab icon to save it
              </p>
              <TryIt
                savedKeys={savedKeys}
                selectedKey={selectedKey}
                popoverKey={popoverKey}
                hoverKey={hoverKey}
                onWordSelect={handleWordSelect}
                onIconClick={handleIconClick}
                onClosePopover={handleClosePopover}
                onWordHover={setHoverKey}
                onSave={handleSave}
              />
            </div>
          </div>
        </div>

        {/* extension popup mockup */}
        <div className="col-lg-5">
          <div
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 16,
              boxShadow: '0 8px 28px rgba(15,23,42,0.06)',
              height: 460,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 14px', borderBottom: '1px solid #f1f3f5' }}>
              <div style={{ width: 18, height: 18, borderRadius: 5, background: '#7c3aed' }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>HighVocab</span>
              <span style={{ marginLeft: 'auto', fontSize: 10, color: '#94a3b8' }}>{savedWords.length} saved</span>
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
              {detailWord ? (
                <WordDetail word={detailWord} onBack={() => setDetailKey(null)} />
              ) : (
                savedWords.map((w) => (
                  <WordRow key={w.key} word={w} flash={flashKey === w.key} onClick={() => setDetailKey(w.key)} />
                ))
              )}
            </div>

            {!detailWord && <PopupTabBar />}
          </div>
        </div>
      </div>
    </div>
  );
}
